import { Link, useNavigate, useParams } from "react-router-dom";
import { protocolsApi } from "../../services/protocols/protocolsApi";
import { Button } from "primereact/button";
import { FormikProvider, useFormik } from "formik";
import { Skeleton } from "primereact/skeleton";
import Error from "../../components/common/Error";
import CustomInputText from "../../components/forms/CustomInputText";
import { Card } from "primereact/card";
import CustomDatepicker from "../../components/forms/CustomDatepicker";
import { ProtocolResponse, ProtocolStudentFilter, ProtocolSubjectResponse, RecognitionProtocolResponse } from "../../models/dtos";
import CustomDropdown from "../../components/forms/CustomDropdown";
import { teachersApi } from "../../services/teachers/teachersApi";
import { specialitiesApi } from "../../services/nomenclatures/specialitiesApi";
import { academicYearsApi } from "../../services/nomenclatures/academicYearsApi";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import ProtocolGradesList from "./ProtocolGradesList";
import { examTypesApi } from "../../services/nomenclatures/examTypesApi";
import ItemBrowser from "../../components/forms/ItemBrowser";
import ActionButtons from "./ActionButtons";
import ProtocolSubjectsList from "./ProtocolSubjectsList";
import { TabPanel } from "primereact/tabview";
import { TabView } from "primereact/tabview";
import ProtocolCommission from "./ProtocolCommission";
import ProtocolVersionsList from "./ProtocolVersionsList";
import { Tag } from "primereact/tag";
import { studentsApi } from "../../services/students/studentsApi";
import ProtocolGradeSubjectsList from "./ProtocolGradeSubjectsList";

const ProtocolDetailsRecognition = () => {
  const { id } = useParams();
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  const {
    data: protocol,
    error: protocolError,
    isLoading: protocolLoading,
  } = protocolsApi.useGetRecognitionProtocolQuery(parseInt(id!), {
    skip: !id,
  });

  const [filter, setFilter] = useState<ProtocolStudentFilter>({
    showHigherSemesterStudents: false,
    showForeignStudents: true,
    showStudyingInEnglish: false,
    studentFnOrName: undefined,
  });

  const {
    data: protocolSubjects,
    error: protocolSubjectsError,
    isLoading: protocolSubjectsLoading,
    isFetching: protocolSubjectsFetching,
  } = protocolsApi.useGenerateProtocolSubjectListQuery(
    //на база специалност да се намерят подходящите дисциплини
    {
      protocolId: parseInt(id!),
    },
    {
      skip: !id,
    },
  );

  const {
    data: protocolVersions,
    error: protocolVersionsError,
    isLoading: protocolVersionsLoading,
  } = protocolsApi.useGetProtocolVersionsQuery(parseInt(id!), {
    skip: !id,
  });

  const [updateProtocol, { isLoading: isUpdateLoading, error: updateError, isSuccess: isUpdateSuccess }] =
    protocolsApi.useUpdateRecognitionProtocolMutation();

  const { data: teachers } = teachersApi.useGetTeachersQuery({});
  const { data: specialities } = specialitiesApi.useGetSpecialitiesAsFlatListQuery();
  const { data: examTypes } = examTypesApi.useGetExamTypesQuery();
  const { data: academicYears } = academicYearsApi.useGetAcademicYearsQuery();

  const [triggerGetStudentsBySpecialityIdQuery] = studentsApi.useLazyGetStudentsBySpecialityIdQuery();

  const [setProtocolSubjects, { isLoading: isSetProtocolSubjectsLoading, error: setProtocolSubjectsError }] =
    protocolsApi.useSetProtocolSubjectsMutation();

  const [selectedSubjects, setSelectedSubjects] = useState<ProtocolSubjectResponse[]>([]);

  const handleSelectionChange = (subjects: ProtocolSubjectResponse[]) => {
    setSelectedSubjects(subjects);
  };

  const handleAddToProtocol = () => {
    setProtocolSubjects({ protocolId: parseInt(id!), protocolSubjects: selectedSubjects });
  };

  const formik = useFormik({
    initialValues: {
      ...protocol?.data,
      issueDate: protocol?.data.issueDate ? new Date(protocol.data.issueDate) : null,
      examDate: protocol?.data.examDate ? new Date(protocol.data.examDate) : null,
    } as Partial<RecognitionProtocolResponse>,
    enableReinitialize: true,
    onSubmit: (values) => {
      updateProtocol(values as Partial<ProtocolResponse>)
        .unwrap()
        .then((result) => {
          if (result && "data" in result) {
            navigate(`/protocols/${id ? id : result.data}`);
          }
        });
    },
  });

  const availableSubstituteTeachers = teachers?.data.filter((teacher) => teacher.teacherId !== formik.values.teacherId);

  useEffect(() => {
    if (isUpdateSuccess) {
      toast.current?.show({ severity: "success", summary: "Обновяване", detail: "Протоколът е обновен успешно." });
    }
  }, [isUpdateSuccess]);

  if (protocolLoading) return <Skeleton className="w-full h-2rem" />;
  if (protocolError) return <Error error={protocolError} />;

  return (
    <FormikProvider value={formik}>
      <div>
        <Toast ref={toast} />

        {isUpdateLoading && <ProgressSpinner />}
        {updateError && <Error error={updateError} />}

        <form onSubmit={formik.handleSubmit}>
          <Card
            title={`Протокол ${protocol?.data.protocolNumber || ""}`}
            footer={<Button type="submit" label="Save" icon="pi pi-save" />}
            className="mb-4 shadow-4"
            pt={{
              footer: {
                className: "flex justify-content-center",
              },
            }}
          >
            <div className="grid">
              <div className="col-4">
                <div className="grid">
                  <div className="field col-4 flex flex-column">
                    <label htmlFor="currentVersion">Текуща версия</label>
                    <span id="currentVersion">{protocol?.data.currentVersion || "-"}</span>
                  </div>
                  <div className="field col-4 flex flex-column">
                    <label htmlFor="currentStatus">Текущ статус</label>
                    <Tag
                      id="currentStatus"
                      value={protocol?.data.currentStatus || "-"}
                      severity={protocol?.data.currentStatus === "SIGNED" ? "success" : "info"}
                    />
                  </div>
                  <div className="field col-4 flex flex-column">
                    <label htmlFor="isSigned">Подписан</label>
                    <span id="isSigned">{protocol?.data.isSigned ? "Да" : "Не"}</span>
                  </div>
                  <div className="field col-4 flex flex-column">
                    <label htmlFor="signedBy">Подписан от</label>
                    <span id="signedBy">{protocol?.data.signedBy || "-"}</span>
                  </div>
                  <div className="field col-4 flex flex-column">
                    <label htmlFor="signedOn">Дата на подписване</label>
                    <span id="signedOn">
                      {protocol?.data.signedOn ? new Date(protocol.data.signedOn).toLocaleDateString("bg-BG") : "-"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-8">
                <Card>
                  <ActionButtons protocolId={parseInt(id!)} />
                </Card>
              </div>
              <div className="col-12 md:col-4">
                <CustomInputText id="protocolNumber" label="Номер на протокол" />
              </div>
              <div className="col-12 md:col-4">
                <div className="flex flex-row align-items-center gap-2">
                  <div className="flex-1">
                    <CustomDropdown
                      id="teacherId"
                      label="Преподавател"
                      options={teachers?.data || []}
                      optionLabel={(option) =>
                        `${option.firstName} ${option.middleName} ${option.lastName} - ${option.academicPositionShortName || ""} ${option.academicRankShortName || ""} - ${option.departmentFacultyShortName || ""}`
                      }
                      optionValue="teacherId"
                      filter
                    />
                  </div>
                  {formik.values.teacherId && (
                    <Link to={`/teachers/${formik.values.teacherId}`}>
                      <Button
                        icon="pi pi-user"
                        className="p-button-info p-button-text"
                        tooltip="Към профила на преподавателя"
                      />
                    </Link>
                  )}
                </div>
              </div>

              <div className="col-12 md:col-4">
                <div className="flex flex-row align-items-center gap-2">
                  <div className="flex-1">
                    <CustomDropdown
                      id="substituteTeacherId"
                      label="Преподавател (подписващ)"
                      options={availableSubstituteTeachers || []}
                      optionLabel={(option) =>
                        `${option.firstName} ${option.middleName} ${option.lastName} - ${option.academicPositionShortName || ""} ${option.academicRankShortName || ""} - ${option.departmentFacultyShortName || ""}`
                      }
                      optionValue="teacherId"
                      filter
                    />
                  </div>
                  {formik.values.substituteTeacherId && (
                    <Link to={`/teachers/${formik.values.substituteTeacherId}`}>
                      <Button
                        icon="pi pi-user"
                        className="p-button-info p-button-text"
                        tooltip="Към профила на преподавателя"
                      />
                    </Link>
                  )}
                </div>
              </div>

              <div className="col-12 md:col-4">
                <CustomDropdown
                  id="specialityId"
                  label="Специалност"
                  options={specialities?.data || []}
                  optionLabel="name"
                  optionValue="specialityId"
                  filter
                />
              </div>

              <div className="col-12 md:col-4">
                <div className="flex flex-row align-items-center gap-2">
                  <div className="flex-1">
                    <ItemBrowser
                      id="studentId"
                      label="Студент"
                      optionsPromise={() => triggerGetStudentsBySpecialityIdQuery(formik.values.specialityId || 0)}
                      idField="studentId"
                      labelField="name"
                      currentLabelText={protocol?.data.studentName || ""}
                      columns={[
                        { field: "facultyNumber", header: "FN", sortable: true, filter: true },
                        {
                          header: "Име",
                          sortable: true,
                          filter: true,
                          body: (rowData: any) =>
                            `${rowData.firstName ?? ""} ${rowData.middleName ?? ""} ${rowData.lastName ?? ""}`.trim(),
                        },
                      ]}
                    />
                  </div>
                  {formik.values.studentId && (
                    <Link to={`/students/${formik.values.studentId}`}>
                      <Button
                        icon="pi pi-user"
                        className="p-button-info p-button-text"
                        tooltip="Към профила на студента"
                      />
                    </Link>
                  )}
                </div>
              </div>

              <div className="col-12 md:col-4">
                <CustomDatepicker id="issueDate" label="Дата на издаване" />
              </div>

              <div className="col-12 md:col-3">
                <CustomDropdown
                  id="academicYearId"
                  label="Академична година"
                  options={academicYears?.data || []}
                  optionLabel="name"
                  optionValue="academicYearId"
                />
              </div>

              <div className="col-12">
                <label>Комисия / преподаватели</label>
                <ProtocolCommission />
              </div>
            </div>
          </Card>
        </form>

        <TabView>
          <TabPanel header="Оценки" leftIcon="pi pi-chart-bar mr-2">
            {protocol?.data && <ProtocolGradeSubjectsList protocol={protocol.data} />}
          </TabPanel>
          <TabPanel header="Списък с дисциплини" leftIcon="pi pi-users mr-2">
            {(protocolSubjectsLoading || protocolSubjectsFetching) && <Skeleton className="w-full h-2rem" />}
            {protocolSubjectsError && <Error error={protocolSubjectsError} />}
            {protocolSubjects?.data && (
              <>
                <ProtocolSubjectsList
                  protocolId={parseInt(id!)}
                  protocolSubjects={protocolSubjects.data}
                  selectedSubjects={selectedSubjects}
                  onSelectionChange={handleSelectionChange}
                />
                <Button label="Добави в протокол" icon="pi pi-plus" onClick={handleAddToProtocol} />
                {isSetProtocolSubjectsLoading && <ProgressSpinner />}
                {setProtocolSubjectsError && <Error error={setProtocolSubjectsError} />}
              </>
            )}
          </TabPanel>
          <TabPanel header="Версии" leftIcon="pi pi-list mr-2">
            {protocolVersionsLoading && <Skeleton className="w-full h-2rem" />}
            {protocolVersionsError && <Error error={protocolVersionsError} />}
            {protocolVersions?.data && (
              <ProtocolVersionsList protocolId={parseInt(id!)} protocolVersions={protocolVersions.data} />
            )}
          </TabPanel>
        </TabView>
      </div>
    </FormikProvider>
  );
};

export default ProtocolDetailsRecognition;
